import { api } from "../base";
import type { IMarkupTier } from "@pulse-ui/codegen/generated_ts/markup_tier";
import type { IClstAccountTier } from "@pulse-ui/codegen/generated_ts/clst_account_tier";

// ── Types ───────────────────────────────────────────────────────────────────

export type MarkupTier = IMarkupTier;
export type AccountTier = IClstAccountTier;

export interface BookOptionsTradeRequest {
  base_asset: string;
  quote_asset: string;
  delivery_asset: string;
  unit_notional_amount: string;
  payoff_type: string;
  strike_price: string;
  payment_ccy: string;
  option_type: string;
  expiry_date: string;
  tick_size: string;
  side: string;
  quantity: string;
  notional_amount: string;
  notional_asset: string;
  premium: string;
  market_account: string;
  counterparty: string;
  sub_account: string;
  initial_margin_pct: string;
  initial_margin_amount: string;
  maintenance_margin_pct: string;
  liquidation_margin_pct: string;
  margin_release_pct: string;
  margin_call_pct: string;
  commission_bps: string;
  commission_currency: string;
}

export interface BookSpotTradeRequest {
  customer: string;
  customer_account: string;
  symbol: string;
  is_buy: boolean;
  quantity: number;
  price: number;
  amount: number;
  commission: number;
  fee: number;
  fee_bps: number;
  external_id: string;
  trade_time: string;
  internal_comments: string;
  risk_sub_account: string;
  market: string;
  fee_currency?: string;
}

export interface MarkupTierOption {
  code: string;
  display_name: string;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

const tradeEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Markup Tiers ──────────────────────────────────────────────────

    /** Fetches all markup tiers. */
    getMarkupTiers: build.query<IMarkupTier[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates a markup tier. */
    createMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Updates a markup tier. */
    updateMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Deletes a markup tier by code. */
    deleteMarkupTier: build.mutation<void, string>({
      query: (tierCode) => ({
        url: "/api/trade/v1/markup-tier",
        method: "DELETE",
        params: { tier_code: tierCode },
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    // ── Account Tiers ─────────────────────────────────────────────────

    /** Fetches all account-tier assignments. */
    getAccountTiers: build.query<IClstAccountTier[], void>({
      query: () => ({ url: "/api/trade/v1/clst-account-tier" }),
      providesTags: ["AccountTiers"],
    }),

    /** Fetches markup tier options (code + display_name) for account-tier dropdowns. */
    getMarkupTierOptions: build.query<MarkupTierOption[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      transformResponse: (response: IMarkupTier[]) =>
        (response ?? []).map((t) => ({
          code: t.code,
          display_name: t.display_name,
        })),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates an account-tier assignment. */
    createAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Updates an account-tier assignment. */
    updateAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Deletes an account-tier assignment. */
    deleteAccountTier: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Books a spot trade via trading-ops. */
    bookSpotTrade: build.mutation<void, BookSpotTradeRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "spot" },
        body,
      }),
    }),

    /** Books an options trade via trading-ops. */
    bookOptionsTrade: build.mutation<void, BookOptionsTradeRequest>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "options" },
        body,
      }),
    }),
  }),
});

export const {
  useGetMarkupTiersQuery,
  useCreateMarkupTierMutation,
  useUpdateMarkupTierMutation,
  useDeleteMarkupTierMutation,
  useGetAccountTiersQuery,
  useGetMarkupTierOptionsQuery,
  useCreateAccountTierMutation,
  useUpdateAccountTierMutation,
  useDeleteAccountTierMutation,
  useBookSpotTradeMutation,
  useBookOptionsTradeMutation,
} = tradeEndpoints;
